import * as React from "react";
import {useMemo, useState} from "react";
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {IPlansTabResponse} from "../../../../api/AdminAnalyticsApi";
import {applySort, SortDir} from "../../../../utils/listUtil";
import {sortTh} from "../shared/SortableTh.component";
import {truncateLabel} from "../shared/analytics.format";
import {numberWithCommas} from "../../../../utils/numberUtil";
import {
    axisTickStyle,
    chartColors,
    gridProps,
    gridStroke,
    legendStyle,
    PLAN_ADOPTION_DESCRIPTION,
    tooltipStyle,
} from "../shared/analytics.constants";

interface IPlanFunnelChartProps {
    rows: IPlansTabResponse["planFunnel"];
}

export const PlanFunnelChart: React.FC<IPlanFunnelChartProps> = ({rows}) => {
    const [planFunnelSort, setPlanFunnelSort] = useState<{ col: string; dir: SortDir }>({
        col: "totalPageViews",
        dir: "desc",
    });
    const sortedPlanFunnel = useMemo(
        () => applySort(rows, planFunnelSort.col, planFunnelSort.dir),
        [rows, planFunnelSort]);

    const avgPagesPerUser = rows.length > 0
        ? Math.round(rows.reduce((sum, r) => sum + r.avgPagesPerUser, 0) / rows.length * 10) / 10
        : 0;

    return (
        <section className="admin-analytics__section">
            <div className="admin-analytics__section-header">
                <h2 className="admin-analytics__section-title">Plan Adoption</h2>
                <span className="admin-analytics__section-subtitle">
                    How deeply are users engaging with each plan?
                </span>
            </div>
            <p className="admin-analytics__section-desc">{PLAN_ADOPTION_DESCRIPTION}</p>
            <div className="admin-analytics__summary-grid">
                <div className="admin-analytics__summary-card admin-analytics__summary-card--total">
                    <div className="admin-analytics__summary-label">Plans with Activity</div>
                    <div className="admin-analytics__summary-value">{rows.length}</div>
                    <div className="admin-analytics__summary-note">plans with user visits</div>
                </div>
                <div className="admin-analytics__summary-card admin-analytics__summary-card--internal">
                    <div className="admin-analytics__summary-label">Avg Pages per User</div>
                    <div className="admin-analytics__summary-value">{avgPagesPerUser}</div>
                    <div className="admin-analytics__summary-note">across top plans</div>
                </div>
            </div>
            <div className="admin-analytics__chart">
                <ResponsiveContainer width="100%"
                                     height={Math.max(300, rows.length * 40 + 60)}>
                    <BarChart data={rows} layout="vertical" margin={{left: 10, bottom: 20}}>
                        <CartesianGrid {...gridProps} />
                        <XAxis type="number" tick={axisTickStyle} axisLine={{stroke: gridStroke}}
                               tickLine={false}/>
                        <YAxis dataKey="planName" type="category" width={180}
                               tick={{fontSize: 11, fill: chartColors.gray02}}
                               tickFormatter={(v: string) => truncateLabel(v)} axisLine={false}
                               tickLine={false}/>
                        <Tooltip {...tooltipStyle} />
                        <Legend {...legendStyle} />
                        <Bar dataKey="totalPageViews" fill={chartColors.tealDark} name="Page Views"/>
                        <Bar dataKey="totalUsers" fill={chartColors.navy} name="Unique Users"/>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <table className="admin-analytics__table">
                <thead>
                <tr>
                    {sortTh("planName", "Plan", planFunnelSort, setPlanFunnelSort, undefined, "asc")}
                    {sortTh("totalUsers", "Users", planFunnelSort, setPlanFunnelSort,
                        "admin-analytics__cell--right")}
                    {sortTh("totalPageViews", "Page Views", planFunnelSort, setPlanFunnelSort,
                        "admin-analytics__cell--right")}
                    {sortTh("avgPagesPerUser", "Avg Pages/User", planFunnelSort, setPlanFunnelSort,
                        "admin-analytics__cell--right")}
                </tr>
                </thead>
                <tbody>{sortedPlanFunnel.map((row) => (
                    <tr key={row.planName}>
                        <td>{row.planName}</td>
                        <td className="admin-analytics__cell--right">{numberWithCommas(row.totalUsers)}</td>
                        <td className="admin-analytics__cell--right">{numberWithCommas(row.totalPageViews)}</td>
                        <td className="admin-analytics__cell--right">{row.avgPagesPerUser}</td>
                    </tr>
                ))}</tbody>
            </table>
        </section>
    );
};
